// src/pages/documentation/TpvAgoraUserGuide.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle,
    faQrcode,
    faCog,
    faKey,
    faCopy,
    faCheck,
    faArrowRight,
    faEnvelope,
    faStore,
    faNetworkWired,
    faDesktop,
    faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const TpvAgoraUserGuide = () => {
    const [copiedItems, setCopiedItems] = useState({});

    const copyToClipboard = async (text, itemId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedItems(prev => ({ ...prev, [itemId]: true }));
            setTimeout(() => {
                setCopiedItems(prev => ({ ...prev, [itemId]: false }));
            }, 2000);
        } catch (err) {
            console.error('Error copying to clipboard:', err);
        }
    };

    const sections = [
        {
            id: 'introduccion',
            title: '¿Qué vas a conseguir?',
            icon: faInfoCircle,
            content: (
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-blue-800 mb-3 text-lg">🎯 Objetivo de esta guía</h4>
                        <p className="text-blue-700 mb-4">
                            Con esta guía aprenderás a configurar tu Terminal de Punto de Venta (TPV) con Agora Software
                            para que tus clientes acumulen puntos automáticamente al presentar su código QR en la caja.
                        </p>
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h5 className="font-semibold text-blue-800 mb-3">✅ Resultado final:</h5>
                            <ul className="space-y-2 text-blue-700">
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1 flex-shrink-0" />
                                    <span>Los clientes escanean su QR → Los puntos se acumulan automáticamente</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1 flex-shrink-0" />
                                    <span>Todo funciona sin intervención manual</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1 flex-shrink-0" />
                                    <span>Los puntos se suman instantáneamente a la cuenta del cliente</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1 flex-shrink-0" />
                                    <span>El cliente ve los puntos acumulados en su tarjeta de fidelización digital</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h4 className="font-semibold text-green-800 mb-3 text-lg">🔄 ¿Cómo funciona?</h4>
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                                <div>
                                    <p className="font-medium text-green-800">Cliente se registra al comercio con el QR o lo registra el negocio</p>
                                    <p className="text-sm text-green-700">El cliente recibe un correo electrónico con su tarjeta digital</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                                <div>
                                    <p className="font-medium text-green-800">Cliente en la tienda</p>
                                    <p className="text-sm text-green-700">El cliente presenta su QR en el TPV al pagar su compra</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                                <div>
                                    <p className="font-medium text-green-800">Agora procesa la venta</p>
                                    <p className="text-sm text-green-700">Agora escanea el QR, procesa la venta y envía los datos a PetitPrime</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                                <div>
                                    <p className="font-medium text-green-800">Puntos se acumulan</p>
                                    <p className="text-sm text-green-700">PetitPrime recibe los datos y suma los puntos automáticamente a la cuenta del cliente</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'requisitos',
            title: '¿Qué necesitas?',
            icon: faCheckCircle,
            content: (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <FontAwesomeIcon icon={faDesktop} className="text-blue-600" />
                                Tu TPV Agora
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                    <span>Software Agora v8.5.0 o superior instalado</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                    <span>Conexión a internet estable</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                    <span>Acceso de administrador al TPV</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                    <span>Escáner de códigos QR configurado</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <FontAwesomeIcon icon={faFileAlt} className="text-purple-600" />
                                Información que necesitarás
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faStore} className="text-purple-600 mt-0.5 flex-shrink-0" />
                                    <span>Nombre del terminal (ej: "TPV Principal - Caja 1")</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faNetworkWired} className="text-purple-600 mt-0.5 flex-shrink-0" />
                                    <span>Dirección IP del TPV (opcional, pero recomendado)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600 mt-0.5 flex-shrink-0" />
                                    <span>Descripción del terminal (opcional)</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
                        <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
                            ⚠️ Importante
                        </h4>
                        <p className="text-yellow-700 text-sm mb-2">
                            Necesitarás acceso a tu cuenta de PetitPrime y permisos para gestionar la configuración de TPV de tu tienda.
                        </p>
                        <p className="text-yellow-700 text-sm">
                            Si no tienes acceso, contacta con el administrador de tu cuenta.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'paso1',
            title: 'Paso 1: Crear la configuración en PetitPrime',
            icon: faCog,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-blue-800 mb-3 text-lg">📍 Ubicación en el panel</h4>
                        <p className="text-blue-700 mb-3">
                            Para crear la configuración del TPV, sigue estos pasos:
                        </p>
                        <ol className="space-y-2 text-blue-700 list-decimal list-inside">
                            <li>Inicia sesión en tu cuenta de PetitPrime</li>
                            <li>Ve a <strong>Tiendas</strong> en el menú principal</li>
                            <li>Selecciona la tienda donde quieres configurar el TPV</li>
                            <li>Haz clic en la pestaña <strong>TPV</strong></li>
                            <li>Haz clic en el botón <strong>"Nueva Configuración"</strong></li>
                        </ol>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h4 className="font-semibold text-green-800 mb-3 text-lg">📝 Completa el formulario</h4>
                        <div className="space-y-4">
                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-2">Campos del formulario:</h5>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <label className="font-medium text-gray-700">Nombre *</label>
                                        <p className="text-gray-600 mt-1">Ejemplo: "TPV Principal - Caja 1"</p>
                                        <p className="text-gray-500 text-xs mt-1">Un nombre descriptivo para identificar este terminal</p>
                                    </div>
                                    <div>
                                        <label className="font-medium text-gray-700">Descripción</label>
                                        <p className="text-gray-600 mt-1">Ejemplo: "Terminal principal en la entrada"</p>
                                        <p className="text-gray-500 text-xs mt-1">Información adicional sobre la ubicación o uso del terminal</p>
                                    </div>
                                    <div>
                                        <label className="font-medium text-gray-700">Tipo de Software *</label>
                                        <p className="text-gray-600 mt-1">Selecciona: "Agora Software"</p>
                                    </div>
                                    <div>
                                        <label className="font-medium text-gray-700">Dirección IP (Opcional)</label>
                                        <p className="text-gray-600 mt-1">Ejemplo: "192.168.1.100"</p>
                                        <p className="text-gray-500 text-xs mt-1">
                                            La IP del TPV. Si no la conoces, puedes dejarla vacía o pedirle ayuda a tu técnico.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <h4 className="font-semibold text-yellow-800 mb-3 text-lg flex items-center gap-2">
                            <FontAwesomeIcon icon={faKey} className="text-yellow-600" />
                            ⚠️ Guarda tu API Key
                        </h4>
                        <p className="text-yellow-700 mb-3">
                            Después de crear la configuración, el sistema te mostrará una <strong>API Key</strong> única.
                        </p>
                        <div className="bg-white p-4 rounded-lg border border-yellow-100">
                            <p className="text-sm text-yellow-800 mb-2 font-medium">🔑 ¿Qué es una API Key?</p>
                            <p className="text-sm text-yellow-700 mb-3">
                                Es como una "llave digital" que permite que Agora se comunique de forma segura con PetitPrime.
                            </p>
                            <div className="bg-gray-50 p-3 rounded border">
                                <p className="text-xs text-gray-600 mb-2">Ejemplo de API Key:</p>
                                <code className="text-sm font-mono text-gray-800 break-all">
                                    pp_agora_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                                </code>
                            </div>
                            <p className="text-sm text-yellow-700 mt-3">
                                <strong>⚠️ IMPORTANTE:</strong> Guarda esta API Key de forma segura. La necesitarás en el siguiente paso para configurar Agora.
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'paso2',
            title: 'Paso 2: Configurar Agora Software',
            icon: faQrcode,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-blue-800 mb-3 text-lg">📋 Información para tu técnico</h4>
                        <p className="text-blue-700 mb-4">
                            Para configurar Agora Software, necesitarás entregar estos datos a tu técnico o configurarlo tú mismo si tienes acceso de administrador.
                        </p>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                            <h5 className="font-medium text-gray-800 mb-3">Datos que necesitas:</h5>
                            <ul className="space-y-2 text-sm text-gray-700">
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faKey} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                    <span><strong>API Key:</strong> La clave que obtuviste en el Paso 1</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <FontAwesomeIcon icon={faNetworkWired} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                    <span><strong>URL del Webhook:</strong> La dirección donde Agora enviará los datos</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h4 className="font-semibold text-green-800 mb-3 text-lg">🔧 Configuración en Agora</h4>
                        <div className="space-y-4">
                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">1. Acceder al icono de Agora</h5>
                                <p className="text-sm text-gray-700 mb-3">
                                    En la esquina inferior derecha del TPV en Windows, encontrarás un icono de Agora como el que se muestra en la imagen.
                                </p>
                                <div className="bg-gray-50 p-3 rounded border">
                                    <img
                                        src="/imgs/agora1.jpeg"
                                        alt="Icono de Agora en la esquina inferior derecha de Windows"
                                        className="w-full rounded-lg border border-gray-200"
                                    />
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">2. Ir a Herramientas → Activar Módulos Adicionales</h5>
                                <p className="text-sm text-gray-700 mb-3">
                                    Haz clic en el icono de Agora y ve a <strong>Herramientas</strong>, luego selecciona <strong>"Activar Módulos Adicionales"</strong>.
                                </p>
                                <div className="bg-gray-50 p-3 rounded border">
                                    <img
                                        src="/imgs/agora2.jpeg"
                                        alt="Menú de Herramientas con opción Activar Módulos Adicionales"
                                        className="w-full rounded-lg border border-gray-200"
                                    />
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">3. Activar Módulo de Servicios de Integración</h5>
                                <p className="text-sm text-gray-700 mb-3">
                                    En la ventana de "Activar Módulos Adicionales", busca y marca la casilla <strong>"Activar Módulo de Servicios de Integración"</strong>.
                                    Luego haz clic en el enlace <strong>"Configurar Servicios de Integración"</strong> que aparece debajo.
                                </p>
                                <div className="bg-gray-50 p-3 rounded border">
                                    <img
                                        src="/imgs/agora3.jpeg"
                                        alt="Ventana de Activar Módulos Adicionales con Módulo de Servicios de Integración marcado"
                                        className="w-full rounded-lg border border-gray-200"
                                    />
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">4. Configurar Servicios de Integración</h5>
                                <div className="space-y-4">
                                    <div>
                                        <h6 className="font-medium text-gray-700 mb-2">2. Habilitar integración con sistema de fidelización</h6>
                                        <div className="space-y-3 text-sm">
                                            <div className="bg-gray-50 p-3 rounded border">
                                                <p className="text-gray-700 mb-2">
                                                    <strong>☑️ Marca esta casilla:</strong> "Habilitar integración con sistema de fidelización"
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-gray-700 mb-2"><strong>URL para validación de participantes:</strong></p>
                                                <div className="bg-gray-50 p-3 rounded border">
                                                    <code className="text-xs font-mono text-gray-800 break-all">
                                                        https://petitprime-agora-proxy.raul-dearriba-garcia.workers.dev/api/proxy/agora/member/{'{member_id}'}?token=TU_API_KEY_AQUI
                                                    </code>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    ⚠️ Reemplaza <code className="bg-gray-100 px-1 rounded">TU_API_KEY_AQUI</code> con tu API Key real
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-gray-700 mb-2"><strong>URL para envío de facturas:</strong></p>
                                                <div className="bg-gray-50 p-3 rounded border">
                                                    <code className="text-xs font-mono text-gray-800 break-all">
                                                        https://petitprime-agora-proxy.raul-dearriba-garcia.workers.dev/api/proxy/webhooks/agora?token=TU_API_KEY_AQUI
                                                    </code>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    ⚠️ Reemplaza <code className="bg-gray-100 px-1 rounded">TU_API_KEY_AQUI</code> con tu API Key real
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h6 className="font-medium text-gray-700 mb-2">3. Documentos a enviar</h6>
                                        <div className="space-y-2 text-sm text-gray-700">
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                                <span><strong>☑️ Marca:</strong> "Enviar facturas"</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400" />
                                                <span className="text-gray-500">☐ Deja sin marcar: "Enviar albaranes"</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400" />
                                                <span className="text-gray-500">☐ Deja sin marcar: "Enviar pedidos"</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h6 className="font-medium text-gray-700 mb-2">4. Guardar configuración</h6>
                                        <p className="text-sm text-gray-700">
                                            Haz clic en <strong>"Aceptar"</strong> para guardar la configuración en Agora.
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 p-3 rounded border mt-4">
                                    <img
                                        src="/imgs/agora4.jpeg"
                                        alt="Ventana de Servicios de Integración con configuración completa"
                                        className="w-full rounded-lg border border-gray-200"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <h4 className="font-semibold text-yellow-800 mb-3 text-lg flex items-center gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
                            ⚠️ Posibles problemas
                        </h4>
                        <div className="space-y-4">
                            <div className="bg-white p-4 rounded-lg border border-yellow-100">
                                <h5 className="font-medium text-gray-800 mb-2">1. Mensaje de permisos de administrador</h5>
                                <p className="text-sm text-gray-700 mb-3">
                                    Si aparece un mensaje indicando que no tienes permisos de administrador, haz clic en <strong>"Sí"</strong> y vuelve a configurar si no se ha guardado la configuración.
                                </p>
                                <div className="bg-gray-50 p-3 rounded border">
                                    <img
                                        src="/imgs/agora5.jpeg"
                                        alt="Mensaje de error de permisos de administrador"
                                        className="w-full rounded-lg border border-gray-200"
                                    />
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-yellow-100">
                                <h5 className="font-medium text-gray-800 mb-2">2. Error de conexión con el sistema de fidelización</h5>
                                <p className="text-sm text-gray-700 mb-3">
                                    Si aparece un mensaje de error similar al de la imagen, revisa que hayas escrito correctamente las 2 URLs y que hayas reemplazado <code className="bg-yellow-100 px-1 rounded">TU_API_KEY_AQUI</code> con tu API Key real.
                                </p>
                                <div className="bg-gray-50 p-3 rounded border">
                                    <img
                                        src="/imgs/agora6.jpeg"
                                        alt="Mensaje de error de conexión con el sistema de fidelización"
                                        className="w-full rounded-lg border border-gray-200"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'paso3',
            title: 'Paso 3: Probar la configuración',
            icon: faCheckCircle,
            content: (
                <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h4 className="font-semibold text-green-800 mb-3 text-lg">🧪 ¿Cómo probar que funciona?</h4>
                        <div className="space-y-4">
                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">1. Genera un QR de prueba</h5>
                                <p className="text-sm text-gray-700 mb-2">
                                    Desde tu panel de PetitPrime, genera un QR de prueba para un cliente.
                                </p>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">2. Escanea el QR en el TPV</h5>
                                <p className="text-sm text-gray-700 mb-2">
                                    Usa el escáner de QR del TPV Agora para escanear el código QR de prueba.
                                </p>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">3. Procesa una venta</h5>
                                <p className="text-sm text-gray-700 mb-2">
                                    Procesa una venta normal en el TPV como lo harías normalmente.
                                </p>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-green-100">
                                <h5 className="font-medium text-gray-800 mb-3">4. Verifica que los puntos se acumulan</h5>
                                <p className="text-sm text-gray-700 mb-2">
                                    Verifica en el correo con la tarjeta digital del cliente o en tu panel de PetitPrime que los puntos se han acumulado correctamente.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-blue-800 mb-3 text-lg">✅ ¿Dónde verificar que funciona?</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-lg border border-blue-100">
                                <h5 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-600" />
                                    En el correo con la tarjeta digital
                                </h5>
                                <p className="text-sm text-gray-600">
                                    El cliente ve todos sus puntos acumulados en el correo que recibe con su tarjeta digital.
                                </p>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-blue-100">
                                <h5 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faCog} className="text-blue-600" />
                                    En tu panel de PetitPrime
                                </h5>
                                <p className="text-sm text-gray-600">
                                    Puedes ver las estadísticas de uso del TPV en la sección TPV de tu tienda.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <h4 className="font-semibold text-yellow-800 mb-3 text-lg flex items-center gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />
                            ⚠️ Si algo no funciona
                        </h4>
                        <ul className="space-y-2 text-sm text-yellow-700">
                            <li className="flex items-start gap-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span>Verifica que la URL del webhook sea correcta y contenga tu API Key real</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span>Asegúrate de que el escáner de QR esté habilitado en Agora</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span>Comprueba que la conexión a internet funcione correctamente</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span>Contacta con soporte si el problema persiste</span>
                            </li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'preguntas',
            title: 'Preguntas frecuentes',
            icon: faInfoCircle,
            content: (
                <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h4 className="font-semibold text-gray-800 mb-2">🤔 ¿Qué es una API Key?</h4>
                        <p className="text-gray-700 text-sm">
                            Es como una "llave digital" que permite que Agora se comunique de forma segura con nuestro sistema.
                            Se genera automáticamente cuando creas la configuración del TPV.
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h4 className="font-semibold text-gray-800 mb-2">🤔 ¿Qué es un webhook?</h4>
                        <p className="text-gray-700 text-sm">
                            Es como un "mensajero automático" que envía información de las ventas desde Agora a nuestro sistema.
                            Cada vez que un cliente escanea su QR, Agora envía los datos del ticket a nuestro webhook.
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h4 className="font-semibold text-gray-800 mb-2">🤔 ¿Qué pasa si cambio de TPV?</h4>
                        <p className="text-gray-700 text-sm">
                            Solo necesitas crear una nueva configuración con los datos del nuevo TPV.
                            La configuración anterior seguirá funcionando hasta que la desactives.
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h4 className="font-semibold text-gray-800 mb-2">🤔 ¿Puedo tener varios TPVs?</h4>
                        <p className="text-gray-700 text-sm">
                            Sí, puedes configurar tantos TPVs como necesites. Cada terminal tendrá su propia API Key y configuración.
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h4 className="font-semibold text-gray-800 mb-2">🤔 ¿Qué pasa si se rompe la conexión?</h4>
                        <p className="text-gray-700 text-sm">
                            No te preocupes. Nuestro sistema guarda todas las transacciones, así que no se pierden puntos.
                            Si el backend cae temporalmente, el TPV puede seguir funcionando gracias al proxy de Cloudflare.
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h4 className="font-semibold text-gray-800 mb-2">🤔 ¿Los puntos se acumulan en tiempo real?</h4>
                        <p className="text-gray-700 text-sm">
                            Sí, los puntos se acumulan automáticamente al escanear el QR.
                            El cliente puede ver sus puntos actualizados inmediatamente.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <FontAwesomeIcon icon={faQrcode} className="text-blue-600 text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Configurar TPV Agora</h1>
                            <p className="text-gray-600">Guía paso a paso para configurar tu Terminal de Punto de Venta</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/integraciones/tpv-agora" className="hover:text-gray-700">Integraciones</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">TPV Agora</span>
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-6">
                    {sections.map((section) => (
                        <div key={section.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <FontAwesomeIcon icon={section.icon} className="text-blue-600" />
                                    <h2 className="text-xl font-semibold text-gray-800">{section.title}</h2>
                                </div>
                            </div>
                            <div className="px-6 py-6">
                                {section.content}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Support Section */}
                <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-blue-800 mb-4">¿Necesitas ayuda?</h3>
                    <p className="text-blue-700 mb-4">
                        Si tienes alguna pregunta o necesitas asistencia adicional, nuestro equipo de soporte está aquí para ayudarte.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <a
                            href="mailto:info@petitstores.com"
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                        >
                            <FontAwesomeIcon icon={faEnvelope} />
                            info@petitstores.com
                        </a>
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default TpvAgoraUserGuide;

